/**
 * @fileoverview Jobs Data Table
 *
 * Purpose: Sortable, filterable table for the Jobs list page.
 * Built on @tanstack/react-table.
 *
 * Features:
 * - Sortable column headers (click to toggle asc/desc)
 * - Four multi-select dropdown filters (Status, Type, Created, Activity) —
 *   popover combobox pattern with the same coloured badges that appear in
 *   each column; the two date filters share cumulative range buckets
 * - Created / Last Activity columns; last activity is derived server-side
 *   from job change log, task events, and document uploads
 * - Columns menu to show/hide columns, persisted per-browser in localStorage
 * - Global search across job/entity name and practitioners
 * - Reset button surfaces when any filter or non-default sort is active
 * - Search + filter choices persist per user and organisation (MOR-2344);
 *   first-time users start on Pre Appointment, Day One, and Active
 * - Row checkboxes + a Precedents-style bottom bar for bulk status updates
 *
 * Used by: routes/_app/_auth/dashboard/_layout.jobs.index.tsx
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, Search, Star, X } from "lucide-react";

import { Logo } from "@/ui/layout/logo";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { FacetedFilter } from "@/ui/composed/filters/faceted-filter";
import { ColumnVisibilityMenu } from "@/ui/composed/data-display/column-visibility-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import {
  JOB_STATUS_LABELS as STATUS_LABELS,
  JOB_STATUS_OPTIONS,
  JOB_STATUS_VARIANTS as STATUS_VARIANTS,
} from "@/lib/job-statuses";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";
import { cn } from "@/utils/misc";
import type { JobStatus } from "@cvx/jobs/schema";
import {
  loadJobsFilterState,
  saveJobsFilterState,
} from "./-jobs-table-filters";
import { JobsBulkActionBar } from "./-ui.jobs-bulk-action-bar";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TeamMember {
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
}

/**
 * Subset of the listJobs return type the table actually needs. Keeping this
 * shape narrow lets us swap the upstream query without touching every column.
 */
export interface JobRow {
  _id: string;
  _creationTime: number;
  jobName?: string;
  jobCode?: string;
  entityName: string;
  appointmentType?: string;
  appointmentDate?: string;
  status: string;
  practitioners: string[];
  teamMembers: TeamMember[];
  /**
   * Derived last-activity timestamp from `jobs.queries.listJobsLastActivity`,
   * merged in by the Jobs page. Undefined until that query resolves — the
   * column renders a placeholder rather than blocking the table.
   */
  lastActivityAt?: number;
  /**
   * Whether the current user has starred this job (`jobs.stars.listByUser`,
   * merged in by the Jobs page). Starred rows pin to the top by default.
   */
  starred?: boolean;
  /**
   * Unseen agent updates for the current user on this job
   * (`notifications.agentActivity.unseenByJob`, merged in by the Jobs page).
   */
  agentActivity?: { running: number; completed: number; failed: number };
}

// ---------------------------------------------------------------------------
// Appointment type metadata (kept in sync with the create-job dialog)
// ---------------------------------------------------------------------------

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  cvl: "Creditors' Voluntary Liquidation",
  mvl: "Members' Voluntary Liquidation",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring",
  voluntary_administration: "Voluntary Administration",
  deed_of_company_arrangement: "DOCA",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

const APPOINTMENT_TYPE_COLORS: Record<string, BadgeColor> = {
  cvl: "blue",
  mvl: "green",
  simplified_liquidation: "teal",
  court_liquidation: "indigo",
  provisional_liquidation: "purple",
  sbr: "amber",
  voluntary_administration: "orange",
  deed_of_company_arrangement: "sky",
  receivership: "violet",
  scheme_of_arrangement: "pink",
  personal_bankruptcy: "red",
  business_bankruptcy: "emerald",
  creditors_petition: "red",
  debtors_petition: "amber",
  part_x_arrangement: "teal",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats an ISO date string (YYYY-MM-DD) to "d MMMM yyyy" (e.g. "2 April 2026").
 * Returns an em-dash for missing dates so sorting still works on a numeric key.
 */
function formatAppointmentDate(dateStr: string | undefined): string {
  if (!dateStr) return "—";
  const [year, month, day] = dateStr.split("-");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Convert ISO appointment date to a numeric timestamp for sortable comparison.
 * Missing dates collapse to -Infinity so they sort below real dates ascending
 * and above real dates descending (i.e. always at the bottom of the list).
 */
function appointmentDateSortValue(dateStr: string | undefined): number {
  if (!dateStr) return Number.NEGATIVE_INFINITY;
  const [year, month, day] = dateStr.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
}

/**
 * Compact absolute date for the Created / Last Activity columns — "2 Apr 2026".
 * Deliberately shorter than the Appointment Date format so the extra two
 * columns don't push the table into horizontal scroll.
 */
function formatShortDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Full date + time, used for the `title` tooltip on relative timestamps. */
function formatDateTime(ms: number): string {
  return new Date(ms).toLocaleString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Date range filter
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Cumulative "within the last N" buckets plus an explicit older-than tail.
 * Ranges deliberately overlap: the faceted filter is multi-select, and a
 * union of overlapping windows still reads as the widest one selected.
 */
const DATE_RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days", days: 7 },
  { value: "30d", label: "Last 30 days", days: 30 },
  { value: "90d", label: "Last 90 days", days: 90 },
  { value: "365d", label: "Last 12 months", days: 365 },
  { value: "older", label: "Older than 12 months", days: null },
] as const;

const DATE_RANGE_FILTER_OPTIONS = DATE_RANGE_OPTIONS.map(
  ({ value, label }) => ({ value, label }),
);

/**
 * Shared filterFn for the Created / Last Activity columns. `value` is the
 * array of selected range keys; a row passes if it falls inside any of them.
 * The timestamp is read off the row's accessor value so one function serves
 * both columns.
 */
function dateRangeFilterFn(timestamp: number | undefined, ranges: string[]) {
  if (ranges.length === 0) return true;
  if (timestamp === undefined) return false;
  const age = Date.now() - timestamp;
  return ranges.some((key) => {
    const range = DATE_RANGE_OPTIONS.find((r) => r.value === key);
    if (!range) return false;
    return range.days === null
      ? age > 365 * DAY_MS
      : age <= range.days * DAY_MS;
  });
}

// ---------------------------------------------------------------------------
// Agent activity pills (MOR-1013) — blue in progress, green done, red failed
// ---------------------------------------------------------------------------

const AGENT_GLOW = {
  running:
    "text-blue-500 drop-shadow-[0_0_5px_rgba(59,130,246,0.9)] animate-pulse",
  completed: "text-emerald-600 drop-shadow-[0_0_5px_rgba(5,150,105,0.9)]",
  failed: "text-red-600 drop-shadow-[0_0_5px_rgba(220,38,38,0.9)]",
} as const;

const AGENT_GLOW_TITLE = {
  running: "in progress",
  completed: "complete, unseen",
  failed: "failed, unseen",
} as const;

/** One Morgan mark per state present, glowing in that state's colour. */
function AgentActivityPills({
  activity,
}: {
  activity: JobRow["agentActivity"];
}) {
  const statuses = activity
    ? (["running", "completed", "failed"] as const).filter(
        (status) => activity[status] > 0,
      )
    : [];
  if (!activity || statuses.length === 0)
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex items-center gap-1.5">
      {statuses.map((status) => (
        <span
          key={status}
          title={`${activity[status]} ${AGENT_GLOW_TITLE[status]}`}
          className="flex items-center gap-0.5"
        >
          <Logo compact className={`size-4 ${AGENT_GLOW[status]}`} />
          {activity[status] > 1 && (
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {activity[status]}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

/**
 * Row-selection checkbox column (MOR-2344). The header checkbox selects every
 * job matching the current search + filters — the rows on screen — and never
 * hidden ones.
 */
const selectColumn: ColumnDef<JobRow> = {
  id: "select",
  header: ({ table }) => {
    const shown = table.getRowModel().rows.length;
    const label = table.getIsAllPageRowsSelected()
      ? `Deselect all ${shown} jobs shown`
      : `Select all ${shown} jobs shown`;
    return (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? "indeterminate"
              : false
        }
        onCheckedChange={(checked) =>
          table.toggleAllPageRowsSelected(checked === true)
        }
        disabled={shown === 0}
        aria-label={label}
        title={label}
      />
    );
  },
  meta: { label: "Select" },
  enableHiding: false,
  enableSorting: false,
  cell: ({ row }) => (
    // Stop the click reaching the row, which would open (or toggle) the job.
    <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(checked) => row.toggleSelected(checked === true)}
        aria-label={`Select ${row.original.jobName || row.original.entityName}`}
      />
    </div>
  ),
};

/**
 * Column defs are built per-table so the star cell can reach the page's
 * toggle handler; every other column is static.
 */
function buildColumns(
  onToggleStar: ((jobId: string) => void) | undefined,
  selectable: boolean,
): ColumnDef<JobRow>[] {
  return [
    ...(selectable ? [selectColumn] : []),
    {
      id: "starred",
      accessorFn: (row) => (row.starred ? 1 : 0),
      // Header text lives in `meta.label` for the Columns menu; the column
      // header itself is just the icon to keep the leading column narrow.
      header: () => <Star className="h-3.5 w-3.5 text-muted-foreground/70" />,
      meta: { label: "Starred" },
      enableHiding: false,
      sortingFn: "basic",
      filterFn: (row, _id, value: boolean) =>
        !value || row.original.starred === true,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar?.(row.original._id);
          }}
          disabled={!onToggleStar}
          className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted"
          title={row.original.starred ? "Unstar this job" : "Star this job"}
          aria-label={
            row.original.starred ? "Unstar this job" : "Star this job"
          }
        >
          <Star
            className={cn(
              "h-3.5 w-3.5 transition-colors",
              row.original.starred
                ? "fill-amber-400 text-amber-400"
                : "text-muted-foreground/40 hover:text-muted-foreground",
            )}
          />
        </button>
      ),
    },
    ...staticColumns,
  ];
}

const staticColumns: ColumnDef<JobRow>[] = [
  {
    accessorKey: "jobName",
    id: "jobName",
    header: "Job Name",
    // Identity column — never hideable, so it stays out of the Columns menu.
    enableHiding: false,
    cell: ({ row }) => (
      <span className="font-medium">{row.original.jobName || "—"}</span>
    ),
    sortingFn: (a, b) =>
      (a.original.jobName ?? "").localeCompare(b.original.jobName ?? ""),
  },
  {
    accessorKey: "jobCode",
    id: "jobCode",
    header: "Job Code",
    meta: { label: "Job Code" },
    cell: ({ row }) =>
      row.original.jobCode ? (
        <span className="whitespace-nowrap font-mono text-xs">
          {row.original.jobCode}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    sortingFn: (a, b) =>
      (a.original.jobCode ?? "").localeCompare(b.original.jobCode ?? ""),
  },
  {
    accessorKey: "entityName",
    id: "entityName",
    header: "Entity Name",
    meta: { label: "Entity Name" },
    cell: ({ row }) => {
      const isPersonal =
        caseTypeFromAppointmentType(row.original.appointmentType) ===
        "personal";
      if (isPersonal) return <span className="text-muted-foreground">—</span>;
      return row.original.entityName;
    },
  },
  {
    accessorKey: "appointmentType",
    id: "appointmentType",
    header: "Type",
    meta: { label: "Type" },
    cell: ({ row }) => {
      const type = row.original.appointmentType;
      if (!type) return <span className="text-muted-foreground">—</span>;
      return (
        <Badge variant="soft" color={APPOINTMENT_TYPE_COLORS[type] ?? "gray"}>
          {APPOINTMENT_TYPE_LABELS[type] ?? type}
        </Badge>
      );
    },
    filterFn: (row, _id, value: string[]) =>
      value.length === 0 ||
      (row.original.appointmentType !== undefined &&
        value.includes(row.original.appointmentType)),
    sortingFn: (a, b) =>
      (
        APPOINTMENT_TYPE_LABELS[a.original.appointmentType ?? ""] ??
        a.original.appointmentType ??
        ""
      ).localeCompare(
        APPOINTMENT_TYPE_LABELS[b.original.appointmentType ?? ""] ??
          b.original.appointmentType ??
          "",
      ),
  },
  {
    id: "appointmentDate",
    accessorFn: (row) => appointmentDateSortValue(row.appointmentDate),
    header: "Appointment Date",
    meta: { label: "Appointment Date" },
    cell: ({ row }) => formatAppointmentDate(row.original.appointmentDate),
    sortingFn: "basic",
  },
  {
    id: "createdAt",
    accessorFn: (row) => row._creationTime,
    header: "Created",
    meta: { label: "Created" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap">
        {formatShortDate(row.original._creationTime)}
      </span>
    ),
    sortingFn: "basic",
    filterFn: (row, _id, value: string[]) =>
      dateRangeFilterFn(row.original._creationTime, value),
  },
  {
    id: "lastActivityAt",
    // Rows still awaiting the activity query sort to the bottom either way.
    accessorFn: (row) => row.lastActivityAt ?? Number.NEGATIVE_INFINITY,
    header: "Last Activity",
    meta: { label: "Last Activity" },
    cell: ({ row }) => {
      const ts = row.original.lastActivityAt;
      if (ts === undefined)
        return <span className="text-muted-foreground">—</span>;
      return (
        <span className="whitespace-nowrap" title={formatDateTime(ts)}>
          {formatDistanceToNow(ts, { addSuffix: true })}
        </span>
      );
    },
    sortingFn: "basic",
    filterFn: (row, _id, value: string[]) =>
      dateRangeFilterFn(row.original.lastActivityAt, value),
  },
  {
    id: "agentActivity",
    accessorFn: (row) => {
      const a = row.agentActivity;
      return a ? a.running + a.completed + a.failed : 0;
    },
    header: "Agents",
    meta: { label: "Agent activity" },
    cell: ({ row }) => (
      <AgentActivityPills activity={row.original.agentActivity} />
    ),
    sortingFn: "basic",
  },
  {
    id: "team",
    header: "Team",
    meta: { label: "Team" },
    enableSorting: false,
    cell: ({ row }) => <TeamAvatarStack members={row.original.teamMembers} />,
  },
  {
    accessorKey: "status",
    id: "status",
    header: "Status",
    meta: { label: "Status" },
    cell: ({ row }) => (
      <Badge variant={STATUS_VARIANTS[row.original.status] ?? "outline"}>
        {STATUS_LABELS[row.original.status] ?? row.original.status}
      </Badge>
    ),
    filterFn: (row, _id, value: string[]) =>
      value.length === 0 || value.includes(row.original.status),
    sortingFn: (a, b) =>
      (STATUS_LABELS[a.original.status] ?? a.original.status).localeCompare(
        STATUS_LABELS[b.original.status] ?? b.original.status,
      ),
  },
];

// ---------------------------------------------------------------------------
// Filter option lookups (label + value pairs for the faceted filters)
// ---------------------------------------------------------------------------

const TYPE_FILTER_OPTIONS = Object.entries(APPOINTMENT_TYPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const STATUS_FILTER_OPTIONS = JOB_STATUS_OPTIONS.map(({ value, label }) => ({
  value,
  label,
}));

// Starred rows pin to the top by default; appointment date orders the rest.
const DEFAULT_SORTING: SortingState = [
  { id: "starred", desc: true },
  { id: "appointmentDate", desc: true },
];

// ---------------------------------------------------------------------------
// Column visibility (persisted per-browser, same pattern as the contacts and
// Precedent Library tables)
// ---------------------------------------------------------------------------

const COLUMN_VISIBILITY_STORAGE_KEY = "jobs-table-hidden-cols";

/**
 * Every column ships visible; the menu is there so each user can pare the
 * table back to what they care about. An empty object means "no overrides".
 */
const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {};

function loadColumnVisibility(): VisibilityState {
  try {
    const raw = window.localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as VisibilityState;
  } catch {
    /* ignore */
  }
  return DEFAULT_COLUMN_VISIBILITY;
}

/**
 * Shared card class — kept in sync with `_layout.settings.docs.tsx` and
 * `_layout.jobs.index.tsx` so the toolbar / table cards visually match the
 * rest of the dashboard surfaces.
 */
const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

// ---------------------------------------------------------------------------
// Main data table
// ---------------------------------------------------------------------------

interface JobsDataTableProps {
  jobs: JobRow[];
  /**
   * Optional header slot rendered at the very top of the card, above the
   * search + filter bar. The Jobs page passes its title + "New Job" action
   * here so the page heading lives inside the table card.
   */
  header?: ReactNode;
  /** Toggle the current user's star on a job (MOR-932). */
  onToggleStar?: (jobId: string) => void;
  /**
   * localStorage key the search + filters persist under — scoped to the user
   * and organisation by the page (see `jobsFilterStorageKey`).
   */
  filterStorageKey: string;
  /**
   * Apply `status` to the selected jobs (MOR-2344). Resolves with the ids of
   * the jobs that failed to update, which stay selected. Omit to hide the
   * selection checkboxes.
   */
  onBulkUpdateStatus?: (jobs: JobRow[], status: JobStatus) => Promise<string[]>;
}

export function JobsDataTable({
  jobs,
  header,
  onToggleStar,
  filterStorageKey,
  onBulkUpdateStatus,
}: JobsDataTableProps) {
  const navigate = useNavigate();
  const selectable = onBulkUpdateStatus !== undefined;
  const columns = useMemo(
    () => buildColumns(onToggleStar, selectable),
    [onToggleStar, selectable],
  );
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  // Restored once on mount; the page remounts the table if the key changes.
  const [initialFilters] = useState(() =>
    loadJobsFilterState(filterStorageKey),
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialFilters.columnFilters,
  );
  const [globalFilter, setGlobalFilter] = useState(initialFilters.globalFilter);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkPending, setBulkPending] = useState(false);

  useEffect(() => {
    saveJobsFilterState(filterStorageKey, { globalFilter, columnFilters });
  }, [filterStorageKey, globalFilter, columnFilters]);
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(loadColumnVisibility);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(columnVisibility),
      );
    } catch {
      /* ignore */
    }
  }, [columnVisibility]);

  const table = useReactTable({
    data: jobs,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      rowSelection,
    },
    getRowId: (row) => row._id,
    enableRowSelection: selectable,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: (row, _columnId, filterValue: string) => {
      const query = filterValue.trim().toLowerCase();
      if (!query) return true;
      return (
        (row.original.jobName ?? "").toLowerCase().includes(query) ||
        (row.original.jobCode ?? "").toLowerCase().includes(query) ||
        row.original.entityName.toLowerCase().includes(query) ||
        row.original.practitioners.some((p) => p.toLowerCase().includes(query))
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const filteredCount = table.getFilteredRowModel().rows.length;
  const totalCount = jobs.length;

  // Only selections still on screen count — narrowing the filters (or a job
  // leaving them after a status change) drops a row out of the bulk scope.
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  const selectionActive = selectable && selectedCount > 0;

  const clearSelection = useCallback(() => setRowSelection({}), []);

  const handleBulkUpdateStatus = (status: JobStatus) => {
    if (!onBulkUpdateStatus || selectedRows.length === 0) return;
    setBulkPending(true);
    void onBulkUpdateStatus(
      selectedRows.map((row) => row.original),
      status,
    )
      .then((failedIds) =>
        // Keep the failures selected so the user can see and retry them.
        setRowSelection(
          Object.fromEntries(failedIds.map((id) => [id, true] as const)),
        ),
      )
      .finally(() => setBulkPending(false));
  };

  const isFiltered =
    columnFilters.length > 0 ||
    globalFilter.trim().length > 0 ||
    sorting.length !== DEFAULT_SORTING.length ||
    sorting.some(
      (s, i) =>
        s.id !== DEFAULT_SORTING[i]?.id || s.desc !== DEFAULT_SORTING[i]?.desc,
    );

  const resetAll = () => {
    setSorting(DEFAULT_SORTING);
    setColumnFilters([]);
    setGlobalFilter("");
  };

  return (
    <div
      className={cn(
        GROUP_CARD_CLASS,
        // Bound the card to the viewport and lay it out as a column so the
        // table body scrolls internally while the toolbar + column headers
        // stay pinned. 7rem ≈ app header (4rem) + page padding (top/bottom).
        "flex max-h-[calc(100svh-7rem)] flex-col",
      )}
    >
      {/* Search + filter bar — stays pinned at the top of the card */}
      <div className="flex shrink-0 flex-col gap-2 px-5 py-4">
        {header}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
            <Input
              placeholder="Search by job, entity, or practitioner..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="h-9 pl-9 pr-9 text-sm"
            />
            {globalFilter && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setGlobalFilter("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-primary/40 hover:bg-secondary/60 hover:text-primary/70"
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          {(() => {
            const starredOnly =
              (table.getColumn("starred")?.getFilterValue() as
                boolean | undefined) ?? false;
            return (
              <Button
                type="button"
                variant={starredOnly ? "secondary" : "outline"}
                size="sm"
                className="h-9 gap-1.5"
                aria-pressed={starredOnly}
                title={
                  starredOnly
                    ? "Show all jobs"
                    : "Show only jobs you have starred"
                }
                onClick={() =>
                  table
                    .getColumn("starred")
                    ?.setFilterValue(starredOnly ? undefined : true)
                }
              >
                <Star
                  className={cn(
                    "h-3.5 w-3.5",
                    starredOnly && "fill-amber-400 text-amber-400",
                  )}
                />
                Starred
              </Button>
            );
          })()}
          <FacetedFilter
            title="Status"
            options={STATUS_FILTER_OPTIONS}
            selectedValues={
              (table.getColumn("status")?.getFilterValue() as string[]) ?? []
            }
            onSelectionChange={(v) =>
              table
                .getColumn("status")
                ?.setFilterValue(v.length ? v : undefined)
            }
            facetCounts={table.getColumn("status")?.getFacetedUniqueValues()}
            renderLabel={(option) => (
              <Badge variant={STATUS_VARIANTS[option.value] ?? "outline"}>
                {option.label}
              </Badge>
            )}
          />
          <FacetedFilter
            title="Type"
            options={TYPE_FILTER_OPTIONS}
            selectedValues={
              (table
                .getColumn("appointmentType")
                ?.getFilterValue() as string[]) ?? []
            }
            onSelectionChange={(v) =>
              table
                .getColumn("appointmentType")
                ?.setFilterValue(v.length ? v : undefined)
            }
            facetCounts={table
              .getColumn("appointmentType")
              ?.getFacetedUniqueValues()}
            renderLabel={(option) => (
              <Badge
                variant="soft"
                color={APPOINTMENT_TYPE_COLORS[option.value] ?? "gray"}
                className="whitespace-nowrap"
              >
                {option.label}
              </Badge>
            )}
          />
          <FacetedFilter
            title="Created"
            options={DATE_RANGE_FILTER_OPTIONS}
            selectedValues={
              (table.getColumn("createdAt")?.getFilterValue() as string[]) ?? []
            }
            onSelectionChange={(v) =>
              table
                .getColumn("createdAt")
                ?.setFilterValue(v.length ? v : undefined)
            }
          />
          <FacetedFilter
            title="Activity"
            options={DATE_RANGE_FILTER_OPTIONS}
            selectedValues={
              (table
                .getColumn("lastActivityAt")
                ?.getFilterValue() as string[]) ?? []
            }
            onSelectionChange={(v) =>
              table
                .getColumn("lastActivityAt")
                ?.setFilterValue(v.length ? v : undefined)
            }
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-primary/50">
              {filteredCount === totalCount
                ? `${totalCount} job${totalCount === 1 ? "" : "s"}`
                : `${filteredCount} of ${totalCount}`}
              {selectionActive && ` · ${selectedCount} selected`}
            </span>
            {isFiltered && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetAll}
                className="h-7 px-2 text-xs text-primary/60"
              >
                Clear all
              </Button>
            )}
            <ColumnVisibilityMenu table={table} />
          </div>
        </div>
      </div>

      {/* Table — the scroll wrapper fills the remaining card height so the
          body scrolls under the sticky column headers. */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col border-t border-border",
          // Leave room so the floating bulk bar never covers the last row.
          selectionActive && "pb-16",
        )}
      >
        <Table containerClassName="h-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className="sticky top-0 z-10 bg-background shadow-[inset_0_-1px_0_hsl(var(--border))]"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-1.5 text-left transition-colors hover:text-foreground"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {sorted === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : sorted === "desc" ? (
                            <ArrowDown className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                          )}
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No jobs match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  onClick={() =>
                    // While selecting, a row click toggles it (as in the
                    // Precedent Library) rather than opening the job.
                    selectionActive
                      ? row.toggleSelected()
                      : navigate({
                          to: "/dashboard/jobs/$jobId",
                          params: { jobId: row.original._id },
                        })
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selectionActive && (
        <JobsBulkActionBar
          selectedCount={selectedCount}
          pending={bulkPending}
          onUpdateStatus={handleBulkUpdateStatus}
          onDone={clearSelection}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team Avatar Stack
// ---------------------------------------------------------------------------

const MAX_VISIBLE_AVATARS = 3;

function TeamAvatarStack({ members }: { members: TeamMember[] }) {
  if (members.length === 0) {
    return <span className="text-sm text-muted-foreground">--</span>;
  }

  const visible = members.slice(0, MAX_VISIBLE_AVATARS);
  const overflow = members.length - MAX_VISIBLE_AVATARS;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="flex items-center -space-x-2"
            onClick={(e) => e.stopPropagation()}
          >
            {visible.map((member) => (
              <UserAvatar
                key={member.email}
                avatarUrl={member.avatarUrl}
                firstName={member.firstName}
                lastName={member.lastName}
                email={member.email}
                className="h-7 w-7 ring-2 ring-background"
              />
            ))}
            {overflow > 0 && (
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-medium ring-2 ring-background">
                +{overflow}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[240px]">
          <div className="flex flex-col gap-0.5">
            {members.map((member) => (
              <span key={member.email} className="text-xs">
                {getDisplayName(
                  member.firstName,
                  member.lastName,
                  member.email,
                )}
              </span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
